import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import type { MissedAppointment } from '../api-modules';
import { useI18n } from '../i18n';
import { Spinner } from '../components';
import { IconAlert } from '../icons';

/**
 * Missed appointments to follow up. On the dashboard (open ones only) and in
 * the reports (every missed appointment of the period, followed up or not).
 * Reception records what was done: contacted, rebooked, or closed. Booking the
 * patient again closes it by itself.
 */
export function MissedFollowups({
  rows: given,
  canWrite,
  compact = false,
  onChanged,
}: {
  /** Rows from a report; absent = the open list, loaded here. */
  rows?: MissedAppointment[];
  canWrite: boolean;
  compact?: boolean;
  onChanged?: () => void;
}) {
  const { t, tx, formatDate, formatTime, personName } = useI18n();
  const [rows, setRows] = useState<MissedAppointment[] | null>(given ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(() => {
    if (given) return;
    api
      .missedAppointments({ open: true })
      .then((r) => setRows(r.missed))
      .catch(() => setRows([]));
  }, [given]);
  useEffect(() => {
    if (given) setRows(given);
    else load();
  }, [given, load]);

  if (!rows) return null;
  if (compact && rows.length === 0) return null;

  const act = async (id: string, status: 'contacted' | 'rebooked' | 'closed' | null) => {
    setBusy(id);
    try {
      await api.followUpAppointment(id, { status, ...(note.trim() && noteFor === id ? { note: note.trim() } : {}) });
      setNoteFor(null);
      setNote('');
      if (given) onChanged?.();
      else load();
    } finally {
      setBusy(null);
    }
  };
  const shown = compact ? rows.slice(0, 6) : rows;

  return (
    <div className={`card missed${compact ? ' is-compact' : ''}`}>
      <div className="card-head">
        <h2>
          <IconAlert size={16} /> {t.missed.title}
          {compact ? <span className="badge warn" style={{ marginInlineStart: 8 }}>{tx(t.missed.count, { n: rows.length })}</span> : null}
        </h2>
      </div>
      {compact ? <p className="muted missed-sub">{t.missed.sub}</p> : null}
      {shown.length === 0 ? (
        <p className="muted" style={{ padding: '0 20px 16px' }}>{t.missed.none}</p>
      ) : (
        <ul className="missed-list">
          {shown.map((m) => {
            const who = m.patient ? personName(m.patient) : (m.requestedBy?.name ?? '—');
            const phone = m.patient?.whatsappPhone ?? m.patient?.phone ?? m.requestedBy?.phone ?? null;
            return (
              <li key={m.id} className={m.open ? 'is-open' : ''}>
                <div className="missed-who">
                  <strong>{who}</strong>
                  {m.patient ? <span className="ltr-value muted"> · {m.patient.patientNumber}</span> : null}
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {tx(t.missed.missedOn, { date: `${formatDate(m.startsAt)} ${formatTime(m.startsAt)}` })} · {m.dentist.name}
                    {m.reason ? ` · ${m.reason}` : ''}
                  </div>
                  {m.nextBooked ? (
                    <div className="missed-state is-ok">{tx(t.missed.nextBooked, { date: formatDate(m.nextBooked) })}</div>
                  ) : m.followup ? (
                    <div className="missed-state">
                      {(t.missed.status as Record<string, string>)[m.followup.status]}
                      {m.followup.by ? ` · ${tx(t.missed.by, { name: m.followup.by })}` : ''}
                      {m.followup.note ? ` · ${m.followup.note}` : ''}
                    </div>
                  ) : null}
                </div>
                <div className="missed-actions">
                  {phone ? (
                    <a className="btn btn-xs btn-ghost ltr-value" href={`tel:${phone.replace(/\s+/g, '')}`}>{phone}</a>
                  ) : null}
                  {canWrite && !m.nextBooked ? (
                    m.followup ? (
                      <button type="button" className="btn btn-xs btn-ghost" disabled={busy === m.id} onClick={() => act(m.id, null)}>
                        {t.missed.reopen}
                      </button>
                    ) : (
                      <>
                        {!compact ? (
                          <input
                            className="missed-note"
                            placeholder={t.missed.note}
                            value={noteFor === m.id ? note : ''}
                            onChange={(e) => {
                              setNoteFor(m.id);
                              setNote(e.target.value);
                            }}
                            maxLength={1000}
                          />
                        ) : null}
                        {(['contacted', 'rebooked', 'closed'] as const).map((st) => (
                          <button key={st} type="button" className="btn btn-xs btn-ghost" disabled={busy === m.id} onClick={() => act(m.id, st)}>
                            {busy === m.id ? <Spinner /> : t.missed.action[st]}
                          </button>
                        ))}
                      </>
                    )
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
